import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Heart, 
  X, 
  Star, 
  Brain, 
  MessageCircle,
  MapPin,
  Sparkles,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import ProfilePhoto from './ProfilePhoto';
import api from '../services/api';

const AIMatchCard = ({ 
  user, 
  compatibilityData,
  onSwipe, 
  className = "" 
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [conversationStarters, setConversationStarters] = useState([]);
  const [isLoadingStarters, setIsLoadingStarters] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const photos = user?.images?.length ? user.images : (user?.profile_images || []);

  const getConversationStarters = async () => {
    if (!user?.id || conversationStarters.length > 0) return;
    
    setIsLoadingStarters(true);
    try {
      const response = await api.get(`/ai-matching/conversation-starters/${user.id}`);
      setConversationStarters(response.data.conversation_starters || []);
    } catch (error) {
      console.error('Error getting conversation starters:', error);
      setConversationStarters([
        "Hi! I noticed we have some similar interests. What's been the highlight of your week?",
        "Your profile caught my attention! What's something you're passionate about lately?",
        "Hey there! I'd love to know more about you. What's your idea of a perfect weekend?"
      ]);
    } finally {
      setIsLoadingStarters(false);
    }
  };

  const handleSwipe = (direction) => {
    if (onSwipe) {
      onSwipe(user, direction);
    }
  };

  const nextImage = () => {
    if (photos.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % photos.length);
    }
  };

  const getCompatibilityColor = (score) => {
    if (score >= 90) return 'text-green-700 bg-green-50';
    if (score >= 80) return 'text-blue-700 bg-blue-50';
    if (score >= 70) return 'text-yellow-700 bg-yellow-50';
    return 'text-gray-700 bg-gray-50';
  };

  const compatibility = compatibilityData?.compatibility_score || 0;
  const interests = user?.interests || [];

  return (
    <Card className={`w-full max-w-sm sm:max-w-md mx-auto overflow-hidden shadow-xl ${className}`} data-testid="swipe-card">
      {/* Hero photo: primary visual element */}
      <div className="relative aspect-[3/4] max-h-[62vh] min-h-[320px] w-full bg-gray-100" data-testid="swipe-card-photo">
        <ProfilePhoto
          key={`${user.id}-${currentImageIndex}`}
          src={photos[currentImageIndex]}
          alt={user.name}
          onClick={nextImage}
          className={photos.length > 1 ? 'cursor-pointer' : ''}
          testId="swipe-card-img"
        />

        {photos.length > 1 && (
          <div className="absolute top-3 left-3 right-3 flex gap-1">
            {photos.map((_, index) => (
              <div key={index} className={`h-1 flex-1 rounded-full ${index === currentImageIndex ? 'bg-white' : 'bg-white/40'}`} />
            ))}
          </div>
        )}

        <div className="absolute top-5 right-3">
          <Badge className={`${getCompatibilityColor(compatibility)} font-bold shadow`} data-testid="swipe-card-compatibility">
            <Brain className="w-3 h-3 mr-1" />
            {compatibility}%
          </Badge>
        </div>

        {/* Info overlay */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent px-4 pt-16 pb-4 text-white">
          <div className="flex items-center gap-2">
            <h3 className="text-2xl sm:text-3xl font-bold leading-tight" data-testid="swipe-card-name-age">
              {user.name}{user.age ? `, ${user.age}` : ''}
            </h3>
            {user.verified && <Star className="w-5 h-5 text-blue-300" />}
          </div>
          {user.location && (
            <p className="text-sm text-white/90 flex items-center mt-0.5" data-testid="swipe-card-location">
              <MapPin className="w-4 h-4 mr-1" />
              {user.location}{typeof user.distance === 'number' && user.distance > 0 ? ` · ${user.distance} mi` : ''}
            </p>
          )}
          {user.bio && (
            <p className="text-sm text-white/90 mt-2 line-clamp-2" data-testid="swipe-card-bio">{user.bio}</p>
          )}
          {interests.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3" data-testid="swipe-card-interests">
              {interests.slice(0, 5).map((interest) => (
                <span key={interest} className="rounded-full bg-white/20 backdrop-blur-sm px-2.5 py-0.5 text-xs font-medium">
                  {interest}
                </span>
              ))}
              {interests.length > 5 && <span className="text-xs text-white/70 self-center">+{interests.length - 5}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Swipe controls */}
      <div className="flex justify-center gap-6 py-4 bg-white">
        <Button
          size="lg"
          variant="outline"
          className="rounded-full w-16 h-16 border-2 border-red-200 hover:border-red-400 hover:bg-red-50 transition-transform hover:scale-105"
          onClick={() => handleSwipe('left')}
          data-testid="swipe-pass-btn"
          aria-label="Pass"
        >
          <X className="h-7 w-7 text-red-500" />
        </Button>
        <Button
          size="lg"
          className="rounded-full w-16 h-16 bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 shadow-lg transition-transform hover:scale-105"
          onClick={() => handleSwipe('right')}
          data-testid="swipe-like-btn"
          aria-label="Like"
        >
          <Heart className="h-7 w-7 text-white" />
        </Button>
      </div>

      <CardContent className="p-4 pt-0">
        <div className="space-y-3">
          {compatibilityData?.explanation && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-lg">
              <div className="flex items-center mb-1">
                <Sparkles className="w-4 h-4 text-purple-600 mr-2" />
                <span className="text-sm font-medium text-purple-800">AI Compatibility Insight</span>
              </div>
              <p className="text-sm text-purple-700">{compatibilityData.explanation}</p>
            </div>
          )}

          {compatibilityData?.shared_interests?.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-2">Shared Interests</p>
              <div className="flex flex-wrap gap-1">
                {compatibilityData.shared_interests.slice(0, 3).map((interest, index) => (
                  <Badge key={index} variant="outline" className="text-xs">{interest}</Badge>
                ))}
              </div>
            </div>
          )}

          <Collapsible open={showDetails} onOpenChange={setShowDetails}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between p-0 h-auto" data-testid="swipe-card-more-details">
                <span className="text-sm font-medium">{showDetails ? 'Less Details' : 'More Details'}</span>
                {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
            
            <CollapsibleContent className="space-y-3 pt-3">
              {user.bio && <p className="text-sm text-gray-700">{user.bio}</p>}

              {compatibilityData?.complementary_traits?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Complementary Traits</p>
                  <div className="flex flex-wrap gap-1">
                    {compatibilityData.complementary_traits.map((trait, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">{trait}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {interests.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">All Interests</p>
                  <div className="flex flex-wrap gap-1">
                    {interests.map((interest, index) => (
                      <Badge key={index} variant="outline" className="text-xs">{interest}</Badge>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-muted-foreground">AI Conversation Starters</p>
                  <Button size="sm" variant="ghost" className="text-xs p-1 h-auto" onClick={getConversationStarters} disabled={isLoadingStarters}>
                    <MessageCircle className="w-3 h-3 mr-1" />
                    {isLoadingStarters ? 'Loading...' : 'Get Ideas'}
                  </Button>
                </div>
                {conversationStarters.length > 0 && (
                  <div className="space-y-2">
                    {conversationStarters.slice(0, 2).map((starter, index) => (
                      <div key={index} className="bg-blue-50 p-2 rounded text-xs text-blue-800">"{starter}"</div>
                    ))}
                  </div>
                )}
              </div>

              {compatibilityData?.relationship_potential && (
                <div className="bg-green-50 p-2 rounded">
                  <p className="text-xs text-green-800">
                    <span className="font-medium">Relationship Potential: </span>
                    {compatibilityData.relationship_potential}
                  </p>
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </div>
      </CardContent>
    </Card>
  );
};

export default AIMatchCard;
